/** Kite tradingsymbol -> TradingView option-chart ticker.
 *
 * Operator request, 2026-08-11: open the contract's chart straight from the
 * trade log instead of retyping the ticker.
 *
 * TradingView's option format is `ROOT + YYMMDD + C|P + STRIKE`, e.g.
 * `NSE:NIFTY260811C24350`. Kite writes the same contract two different ways:
 *
 *     weekly    NIFTY 26 8 11 24350 CE     <- YY, month CODE, DD, strike
 *     monthly   NIFTY 26 AUG  24350 CE     <- no day at all
 *
 * The weekly form carries the full expiry date, so it converts on its own.
 * The MONTHLY form does not, and this deliberately returns null for it rather
 * than guessing the last Thursday/Tuesday: a wrong date silently opens a
 * different contract's chart, which is worse than no link. Callers that DO
 * know the expiry (positional legs carry one) pass it in and monthly works.
 *
 * Every intraday strategy trades weeklies, so in practice the log links.
 */

/** Kite's single-character month codes. Jan-Sep are the digit itself. */
const MONTH_CODE: Record<string, string> = { O: "10", N: "11", D: "12" };

const MONTH_NAME: Record<string, string> = {
  JAN: "01",
  FEB: "02",
  MAR: "03",
  APR: "04",
  MAY: "05",
  JUN: "06",
  JUL: "07",
  AUG: "08",
  SEP: "09",
  OCT: "10",
  NOV: "11",
  DEC: "12",
};

/** Where each index's options live on TradingView, and under what root.
 * SENSEX is `BSX`, NOT `SENSEX` — user-confirmed 2026-07-22, and the one
 * mapping that is not guessable. An unknown root yields no link. */
const VENUE: Record<string, { exchange: string; root: string }> = {
  NIFTY: { exchange: "NSE", root: "NIFTY" },
  BANKNIFTY: { exchange: "NSE", root: "BANKNIFTY" },
  FINNIFTY: { exchange: "NSE", root: "FINNIFTY" },
  MIDCPNIFTY: { exchange: "NSE", root: "MIDCPNIFTY" },
  SENSEX: { exchange: "BSE", root: "BSX" },
  BANKEX: { exchange: "BSE", root: "BKX" },
};

const ROOTS = Object.keys(VENUE).sort((a, b) => b.length - a.length);

interface Parsed {
  underlying: string;
  /** YYMMDD, or null when the symbol alone cannot say (monthly). */
  date: string | null;
  right: "C" | "P";
  strike: string;
}

/** Jan-Sep are the digit itself and need a leading zero; Oct-Dec are the
 * letters O/N/D, which a naive parse would read as part of the DAY. */
function monthNumber(code: string): string {
  const named = MONTH_CODE[code];
  return named ?? `0${code}`;
}

function parseWeekly(underlying: string, rest: string): Parsed | null {
  const m = /^(\d{2})([1-9OND])(\d{2})(\d+)(CE|PE)$/.exec(rest);
  if (m === null) return null;
  const [, yy = "", month = "", dd = "", strike = "", right = ""] = m;
  return {
    underlying,
    date: `${yy}${monthNumber(month)}${dd}`,
    right: right === "CE" ? "C" : "P",
    strike,
  };
}

function parseMonthly(underlying: string, rest: string): Parsed | null {
  const m = /^(\d{2})([A-Z]{3})(\d+)(CE|PE)$/.exec(rest);
  if (m === null) return null;
  const [, , mon = "", strike = "", right = ""] = m;
  // A real month name, but no day — the caller must supply the expiry.
  if (MONTH_NAME[mon] === undefined) return null;
  return { underlying, date: null, right: right === "CE" ? "C" : "P", strike };
}

function parse(tradingsymbol: string): Parsed | null {
  const sym = tradingsymbol.trim().toUpperCase();
  // Longest root first: MIDCPNIFTY and FINNIFTY both END in NIFTY, and
  // matching the short one would leave junk in the date position.
  const underlying = ROOTS.find((r) => sym.startsWith(r));
  if (underlying === undefined) return null;
  const rest = sym.slice(underlying.length);
  return parseWeekly(underlying, rest) ?? parseMonthly(underlying, rest);
}

/** `YYYY-MM-DD` -> `YYMMDD`, or null if it is not that shape. */
function compactDate(expiry: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(expiry.trim());
  if (m === null) return null;
  const [, yyyy = "", mm = "", dd = ""] = m;
  return `${yyyy.slice(2)}${mm}${dd}`;
}

/** The TradingView ticker (`NSE:NIFTY260811C24350`), or null when the
 * contract cannot be converted with certainty. */
export function tradingViewSymbol(
  tradingsymbol: string | null | undefined,
  expiry?: string | null,
): string | null {
  if (!tradingsymbol) return null;
  const parsed = parse(tradingsymbol);
  if (parsed === null) return null;
  // A supplied expiry WINS: it is the authoritative date, and it is the only
  // thing that makes a monthly contract convertible at all.
  const date = (expiry ? compactDate(expiry) : null) ?? parsed.date;
  const venue = VENUE[parsed.underlying];
  if (date === null || venue === undefined) return null;
  return `${venue.exchange}:${venue.root}${date}${parsed.right}${parsed.strike}`;
}

/** The INDEX itself on TradingView, which is a different ticker from the
 * option root above: options trade under `BSX`, but the SENSEX index chart
 * is `BSE:SENSEX`, and NIFTY's index is plain `NSE:NIFTY` (not `NIFTY 50`,
 * which is Kite's quote symbol). Only the three the bot actually trades are
 * listed — an unmapped index yields no link rather than a guessed one. */
const INDEX_TICKER: Record<string, string> = {
  NIFTY: "NSE:NIFTY",
  BANKNIFTY: "NSE:BANKNIFTY",
  SENSEX: "BSE:SENSEX",
};

/** The chart URL for an index (`NIFTY`, `SENSEX`, …), or null if unmapped. */
export function tradingViewIndexUrl(name: string | null | undefined): string | null {
  if (!name) return null;
  const ticker = INDEX_TICKER[name.trim().toUpperCase()];
  return ticker === undefined ? null : chartUrl(ticker);
}

/** The chart URL for a contract, or null when there is nothing to link to. */
export function tradingViewUrl(
  tradingsymbol: string | null | undefined,
  expiry?: string | null,
): string | null {
  const symbol = tradingViewSymbol(tradingsymbol, expiry);
  return symbol === null ? null : chartUrl(symbol);
}

function chartUrl(ticker: string): string {
  return `https://www.tradingview.com/chart/?symbol=${encodeURIComponent(ticker)}`;
}
